import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Briefcase, Layers, Receipt, Wallet, Inbox, FolderClosed, Settings, HelpCircle,
  LogOut, Bell, AlertCircle, Calendar, Video, Loader2, Clock, Sparkles, ShieldCheck, X,
  MoreHorizontal, FileText, ChevronRight, Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import ClientSidebar from "../components/portal/ClientSidebar";

// Sidebar nav. Dashboard & Files are tabs within this page; Services is its own
// route; the rest are placeholders for now.
const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { key: "projects", label: "Projects", icon: Briefcase },
  { key: "services", label: "Services", icon: Layers },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "files", label: "Files", icon: FolderClosed },
];

// ── types ───────────────────────────────────────────────────────────────────
interface Activity { id: string; category: string; priority: string; project: string; description: string; date?: string; status: string; action?: { label: string; type: string; projectId?: string } | null }
interface Meeting { id: string; title: string; rep: string; type: string; date?: string; pkt?: string }
interface RecentProject { _id: string; name: string; subtitle?: string; progressPct: number; milestonesDone: number; milestonesInProgress: number; milestonesRemaining: number; milestonesTotal: number; filesDelivered: number; latestFile?: string | null; latestFileDate?: string | null; lastActivity: string; status: string }
interface Overview {
  profile: { name: string; email?: string; username?: string; company?: string };
  security?: { twoFactorEnabled: boolean };
  workspace: { referralCode?: string | null; onboardingDate?: string; wallet: { balance: number; currency: string } };
  overview: { completed: number; active: number; total: number; completionPct: number };
  milestones: { completed: number; remaining: number; upcoming: number; total: number };
  invoiceSummary: { paid: number; pending: number; upcoming: number; totalSpent: number; paidPct: number; pendingPct: number; upcomingPct: number };
  insights: string[];
  activities: Activity[];
  meetings: Meeting[];
  recentProjects: RecentProject[];
  projects: any[];
}

const money = (n: any, cur = "USD") => `${cur === "USD" ? "$" : cur + " "}${Number(n || 0).toLocaleString()}`;
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const FILTERS = ["All", "Action Required", "Project Updates", "Invoices", "Files", "Messages"];

const statusChip = (s: string) => {
  if (/complete|paid|released/i.test(s)) return "bg-secondary-container text-[#0f5f57]";
  if (/progress|active|new/i.test(s)) return "bg-secondary-container text-[#0f5f57]";
  if (/hold|pending|escrow|submitted/i.test(s)) return "bg-surface-container-high text-on-surface-variant";
  if (/overdue|cancel/i.test(s)) return "bg-error-container text-on-error-container";
  return "bg-surface-container-high text-on-surface-variant";
};

const ClientPortalPage = () => {
  const { authUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [filter, setFilter] = useState("All");
  const [menuOpen, setMenuOpen] = useState(false);
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [rowMenu, setRowMenu] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const navRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    axiosInstance.get<Overview>("/client-portal/overview")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // First-time Welcome Modal (once per account); the tour is launched from it
  // and can be replayed later from the profile menu.
  useEffect(() => {
    if (!data || !authUser?._id) return;
    if (!localStorage.getItem(`cre-portal-welcome-${authUser._id}`)) setShowWelcome(true);
  }, [data, authUser?._id]);

  const dismissWelcome = () => {
    if (authUser?._id) localStorage.setItem(`cre-portal-welcome-${authUser._id}`, "1");
    setShowWelcome(false);
  };
  const startTour = () => { dismissWelcome(); setShowTour(true); };

  // Live clock.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "Local"; }
  }, []);
  const tzAbbr = useMemo(() => {
    try { return new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(now).find((p) => p.type === "timeZoneName")?.value || ""; }
    catch { return ""; }
  }, [now]);

  const greeting = now.getHours() < 12 ? "Good Morning" : now.getHours() < 17 ? "Good Afternoon" : "Good Evening";
  const firstName = (data?.profile.name || authUser?.fullName || "there").split(" ")[0];

  const filteredActivities = useMemo(() => {
    if (!data) return [];
    if (filter === "All") return data.activities;
    if (filter === "Files") return data.activities.filter((a) => a.category === "Files");
    if (filter === "Messages") return data.activities.filter((a) => a.category === "Messages");
    return data.activities.filter((a) => a.category === filter);
  }, [data, filter]);

  const toggle2fa = async () => {
    if (!data) return;
    const enabled = !data.security?.twoFactorEnabled;
    setTwoFaBusy(true);
    try {
      await axiosInstance.post("/client-portal/two-factor", { enabled });
      setData((d) => (d ? { ...d, security: { twoFactorEnabled: enabled } } : d));
      toast.success(enabled ? "Two-factor enabled" : "Two-factor disabled");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not update two-factor");
    } finally { setTwoFaBusy(false); }
  };

  const openProject = (id: string) => {
    const p = data?.projects.find((x) => x._id === id);
    if (p) setDetail(p);
  };
  const soon = () => toast("This section is coming soon.", { icon: "🔜" });

  if (loading) {
    // Keep the sidebar visible; only the main content shows the loader.
    return (
      <div className="min-h-screen bg-[#f8f9ff] flex">
        <ClientSidebar active="dashboard" />
        <div className="flex-1 min-w-0 grid place-items-center"><Loader2 className="animate-spin text-primary" size={36} /></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f8f9ff] p-6 text-center">
        <div className="max-w-sm">
          <p className="text-on-surface font-semibold">Your workspace isn't ready yet.</p>
          <p className="text-on-surface-variant text-sm mt-1">
            This account isn't linked to a client workspace. Please contact your account manager.
          </p>
          {authUser?.email && (
            <p className="text-outline text-xs mt-3">Signed in as <strong>{authUser.email}</strong></p>
          )}
          <button onClick={logout} className="mt-4 text-sm font-semibold text-primary">Sign out</button>
        </div>
      </div>
    );
  }

  const o = data.overview, ms = data.milestones, inv = data.invoiceSummary;
  const donutDash = `${o.completionPct}, 100`;

  return (
    <div className="min-h-screen bg-[#f8f9ff] text-on-surface flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-outline-variant shrink-0 sticky top-0 h-screen">
        <div className="px-6 py-5 flex items-center gap-1.5">
          {authUser?.tenant?.logoUrl ? (
            <img src={authUser.tenant.logoUrl} alt={authUser.tenant.companyName || "logo"} className="h-8 object-contain" />
          ) : (
            <>
              <span className="text-[22px] font-extrabold text-primary tracking-tight">{authUser?.tenant?.companyName || "Omni Workspace"}</span>
              {!authUser?.tenant && <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.15em] mt-1.5">Ecosystem</span>}
            </>
          )}
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {NAV.filter((n) => (authUser?.tenant as any)?.features?.[n.key] !== false).map((n, idx) => {
            const isActive = activeTab === n.key;
            return (
              <button key={n.key} ref={(el) => { navRefs.current[idx] = el; }}
                onClick={() => {
                  if (n.key === "dashboard") setActiveTab("dashboard");
                  else if (n.key === "services") navigate("/portal/services");
                  else if (n.key === "invoices") navigate("/portal/invoices");
                  else if (n.key === "files") navigate("/portal/files");
                  else if (n.key === "inbox") navigate("/portal/inbox");
                  else soon();
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-semibold transition-colors ${isActive ? "bg-primary text-white" : "text-on-surface-variant hover:bg-surface-container-low"}`}>
                <n.icon size={18} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-outline-variant space-y-1">
          <button onClick={soon} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-medium text-on-surface-variant hover:bg-surface-container-low"><Settings size={18} /> Settings</button>
          <button onClick={soon} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-medium text-on-surface-variant hover:bg-surface-container-low"><HelpCircle size={18} /> Support</button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-medium text-error hover:bg-error-container/40"><LogOut size={18} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-outline-variant px-4 lg:px-8 py-3 flex items-center justify-end gap-2">
          <button onClick={soon} className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high"><Bell size={19} /></button>
          <button onClick={soon} className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high"><HelpCircle size={19} /></button>
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="h-9 w-9 rounded-full bg-primary-container text-white grid place-items-center font-bold hover:bg-primary">
              {firstName?.slice(0, 1).toUpperCase()}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-11 w-60 bg-white rounded-xl border border-outline-variant shadow-lg z-30 overflow-hidden">
                <div className="px-4 py-3 border-b border-outline-variant">
                  <p className="text-[13px] font-bold text-on-surface truncate">{data.profile.name}</p>
                  <p className="text-[11px] text-on-surface-variant truncate">{data.profile.email || data.profile.username}</p>
                </div>
                <button onClick={toggle2fa} disabled={twoFaBusy} className="w-full flex items-center justify-between px-4 py-3 text-[13px] hover:bg-surface-container-low">
                  <span className="flex items-center gap-2 text-on-surface"><ShieldCheck size={15} /> Two-factor</span>
                  <span className={`relative w-9 h-5 rounded-full ${data.security?.twoFactorEnabled ? "bg-primary" : "bg-outline-variant"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${data.security?.twoFactorEnabled ? "translate-x-4" : ""}`} />
                  </span>
                </button>
                <button onClick={() => { setMenuOpen(false); setShowTour(true); }} className="w-full flex items-center gap-2 px-4 py-3 text-[13px] text-on-surface hover:bg-surface-container-low"><Sparkles size={15} /> Replay tour</button>
                <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-3 text-[13px] text-error hover:bg-error-container/40"><LogOut size={15} /> Sign out</button>
              </div>
            )}
          </div>
        </header>

   
        {activeTab === "dashboard" && (
        <main className="p-4 lg:p-8 space-y-6 max-w-[1500px]">
          {/* Greeting + clock */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <h1 className="text-[28px] lg:text-[30px] font-bold tracking-tight">{greeting}, {firstName}!</h1>
              <div className="mt-2 h-px w-full max-w-2xl bg-outline-variant" />
            </div>
            <div className="bg-surface-container-low border border-outline-variant rounded-xl px-5 py-3 text-right">
              <div className="flex items-center gap-2 justify-end text-primary font-bold text-[18px]">
                <Clock size={17} />
                {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })} {tzAbbr && `(${tzAbbr})`}
              </div>
              <p className="text-[12px] text-on-surface-variant mt-0.5">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
              <p className="text-[10px] text-outline mt-0.5">{tz}</p>
            </div>
          </div>

          {/* AI Workspace Insights */}
          <div className="bg-white rounded-xl border border-outline-variant card-shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-primary" />
              <h3 className="text-[14px] font-bold text-on-surface">AI Workspace Insights</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.insights.map((s, i) => (
                <span key={i} className="text-[12px] text-on-surface-variant bg-surface-container-low border border-outline-variant rounded-full px-3 py-1.5">{s}</span>
              ))}
            </div>
          </div>

          {/* Performance snapshot — 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Project Overview donut */}
            <div className="bg-white rounded-xl border border-outline-variant card-shadow p-5">
              <h3 className="text-[16px] font-bold text-on-surface mb-4">Project Overview</h3>
              <div className="flex items-center gap-5">
                <div className="relative w-28 h-28 shrink-0">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#e5eeff" strokeWidth="4" />
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#0058c3" strokeWidth="4" strokeDasharray={donutDash} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center text-[20px] font-extrabold text-on-surface">{o.completionPct}%</div>
                </div>
                <div className="flex-1 space-y-2">
                  <Legend dot="bg-secondary" label="Completed" value={o.completed} />
                  <Legend dot="bg-primary" label="Active" value={o.active} />
                  <div className="pt-2 border-t border-outline-variant text-[12px] text-on-surface-variant">Total Projects: <strong className="text-on-surface">{o.total}</strong></div>
                </div>
              </div>
            </div>

            {/* Milestone Progress */}
            <div className="bg-white rounded-xl border border-outline-variant card-shadow p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[16px] font-bold text-on-surface">Milestone Progress</h3>
                <span className="text-[12px] font-bold text-primary">{ms.total ? Math.round((ms.completed / ms.total) * 100) : 0}% Overall</span>
              </div>
              <div className="h-4 w-full bg-surface-container rounded-full overflow-hidden flex">
                <div className="h-full bg-secondary" style={{ width: `${pct(ms.completed, ms.total)}%` }} />
                <div className="h-full bg-[#0d9488]" style={{ width: `${pct(ms.remaining, ms.total)}%` }} />
                <div className="h-full bg-primary" style={{ width: `${pct(ms.upcoming, ms.total)}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <MiniStat label="Completed" value={ms.completed} />
                <MiniStat label="Remaining" value={ms.remaining} />
                <MiniStat label="Upcoming" value={ms.upcoming} />
              </div>
            </div>

            {/* Spending Summary */}
            <div className="bg-white rounded-xl border border-outline-variant card-shadow p-5">
              <h3 className="text-[16px] font-bold text-on-surface mb-4">Spending Summary</h3>
              <div className="h-2 w-full bg-surface-container rounded-full overflow-hidden flex">
                <div className="h-full bg-secondary" style={{ width: `${inv.paidPct}%` }} title="Paid" />
                <div className="h-full bg-error" style={{ width: `${inv.pendingPct}%` }} title="Pending" />
                <div className="h-full bg-primary" style={{ width: `${inv.upcomingPct}%` }} title="Upcoming" />
              </div>
              <div className="mt-4">
                <span className="text-[11px] font-semibold text-on-surface-variant uppercase tracking-wide">Total Amount Spent</span>
                <p className="text-[26px] font-extrabold text-primary leading-tight">{money(inv.totalSpent)}</p>
              </div>
              <div className="flex items-center gap-4 mt-2 text-[11px] text-on-surface-variant">
                <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-secondary inline-block" /> Paid {inv.paid}</span>
                <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-error inline-block" /> Pending {inv.pending}</span>
                <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-primary inline-block" /> Upcoming {inv.upcoming}</span>
              </div>
            </div>
          </div>

          {/* Activity + Meetings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Activity Control Center */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-outline-variant card-shadow p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[16px] font-bold text-on-surface flex items-center gap-2"><AlertCircle size={18} className="text-error" /> Activity Control Center</h3>
                <button onClick={soon} className="text-[13px] font-semibold text-primary hover:underline">View All Alerts</button>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {FILTERS.map((f) => (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-3 py-1 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${filter === f ? "bg-primary text-white" : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest"}`}>
                    {f}
                  </button>
                ))}
              </div>
              <div className="space-y-2.5">
                {filteredActivities.length === 0 ? (
                  <p className="text-center py-10 text-outline text-sm">No activities in this view.</p>
                ) : filteredActivities.map((a) => {
                  const high = a.priority === "High";
                  return (
                    <div key={a.id} className={`rounded-lg border p-3.5 flex items-start gap-3 ${high ? "border-l-4 border-l-error border-y-outline-variant border-r-outline-variant bg-error-container/20" : "border-outline-variant"}`}>
                      <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${high ? "bg-error" : a.category === "Invoices" ? "bg-primary" : "bg-secondary"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[14px] font-bold text-on-surface truncate">{a.project}</p>
                          <span className="text-[11px] text-outline">· {relTimeLabel(a.date)}</span>
                        </div>
                        <p className="text-[13px] text-on-surface-variant mt-0.5">{a.description}</p>
                      </div>
                      <div className="shrink-0">
                        {a.action ? (
                          <button onClick={() => a.action?.projectId ? openProject(a.action.projectId) : soon()}
                            className="px-3 py-2 rounded-lg bg-primary text-white text-[12px] font-semibold hover:bg-[#004b9e]">{a.action.label}</button>
                        ) : (
                          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusChip(a.status)}`}>{a.status}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Scheduled Meetings */}
            <div className="bg-white rounded-xl border border-outline-variant card-shadow p-5">
              <h3 className="text-[16px] font-bold text-on-surface flex items-center gap-2 mb-4"><Calendar size={18} className="text-primary" /> Scheduled Meetings</h3>
              <div className="space-y-4">
                {data.meetings.length === 0 ? (
                  <p className="text-center py-8 text-outline text-sm">No upcoming meetings.</p>
                ) : data.meetings.map((m) => (
                  <div key={m.id} className="border-l-2 border-primary pl-3">
                    <p className="text-[15px] font-bold text-on-surface leading-tight">{m.title}</p>
                    <p className="text-[12px] text-on-surface-variant">Rep: {m.rep}</p>
                    <p className="text-[11px] text-outline">{m.type}</p>
                    <p className="text-[12px] font-bold text-primary mt-1">{fmtDate(m.date)}</p>
                    {m.date && (
                      <p className="text-[11px] text-secondary font-semibold">
                        {new Date(m.date).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })}{tzAbbr && ` (${tzAbbr})`} · your time
                      </p>
                    )}
                    {m.pkt && <p className="text-[11px] text-on-surface-variant">{m.pkt}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={soon} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-[12px] font-semibold hover:bg-[#004b9e]"><Video size={13} /> Join</button>
                      <button onClick={soon} className="px-3 py-1.5 rounded-lg border border-outline-variant text-[12px] font-semibold text-on-surface-variant hover:bg-surface-container-low">Details</button>
                    </div>
                    <button onClick={soon} className="text-[11px] font-semibold text-primary mt-1.5 hover:underline">Request Reschedule</button>
                  </div>
                ))}
                <button onClick={soon} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#004b9e]"><Calendar size={15} /> Schedule a Meeting</button>
              </div>
            </div>
          </div>

          {/* Recent Projects */}
          <div className="bg-white rounded-xl border border-outline-variant card-shadow overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-outline-variant">
              <h3 className="text-[16px] font-bold text-on-surface">Recent Projects</h3>
              <button onClick={soon} className="p-2 rounded-lg bg-surface-container-low text-on-surface-variant hover:bg-surface-container"><Filter size={15} /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-on-surface-variant border-b border-outline-variant">
                    <Th>Project</Th><Th>Progress</Th><Th>Milestones</Th><Th>Files Delivered</Th><Th>Last Activity</Th><Th>Status</Th><Th>Action</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {data.recentProjects.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-outline">No projects yet.</td></tr>
                  ) : data.recentProjects.map((p) => (
                    <tr key={p._id} className="hover:bg-surface-container-low/50">
                      <td className="px-5 py-3.5">
                        <button onClick={() => openProject(p._id)} className="text-left">
                          <p className="font-bold text-primary hover:underline">{p.name}</p>
                          {p.subtitle && <p className="text-[11px] text-on-surface-variant">{p.subtitle}</p>}
                        </button>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 w-32">
                          <div className="flex-1 h-1.5 bg-surface-container rounded-full overflow-hidden">
                            <div className={`h-full ${p.progressPct === 100 ? "bg-secondary" : "bg-primary"}`} style={{ width: `${p.progressPct}%` }} />
                          </div>
                          <span className="text-[12px] font-semibold text-on-surface">{p.progressPct}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5 text-[11px]">
                          <span className="text-secondary font-semibold">✓ {p.milestonesDone} Completed</span>
                          <span className="text-primary">◐ {p.milestonesInProgress} In Progress</span>
                          <span className="text-on-surface-variant">○ {p.milestonesRemaining} Remaining</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-semibold text-on-surface">{p.filesDelivered} Files</span>
                        {p.latestFile ? <span className="block text-[11px] text-outline truncate max-w-[150px]">{p.latestFile}</span> : null}
                        {p.latestFileDate ? <span className="block text-[10px] text-outline">{fmtDate(p.latestFileDate)}</span> : null}
                      </td>
                      <td className="px-5 py-3.5 text-on-surface-variant">{p.lastActivity}</td>
                      <td className="px-5 py-3.5"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusChip(p.status)}`}>{p.status}</span></td>
                      <td className="px-5 py-3.5 relative">
                        <button onClick={() => setRowMenu(rowMenu === p._id ? null : p._id)} className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-low"><MoreHorizontal size={16} /></button>
                        {rowMenu === p._id && (
                          <div className="absolute right-5 top-12 z-20 w-44 bg-white rounded-lg border border-outline-variant shadow-lg overflow-hidden">
                            <button onClick={() => { setRowMenu(null); openProject(p._id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-on-surface hover:bg-surface-container-low"><Briefcase size={14} /> View Project</button>
                            <button onClick={() => { setRowMenu(null); openProject(p._id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-on-surface hover:bg-surface-container-low"><FolderClosed size={14} /> Open Files</button>
                            <button onClick={() => { setRowMenu(null); openProject(p._id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-on-surface hover:bg-surface-container-low"><Layers size={14} /> View Timeline</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
        )}
      </div>

      {detail && <ProjectDetailModal project={detail} onClose={() => setDetail(null)} />}
      {showWelcome && <WelcomeModal name={data.profile.name} onStartTour={startTour} onSkip={dismissWelcome} />}
      {showTour && <WorkspaceTour navRefs={navRefs} onClose={() => setShowTour(false)} />}
    </div>
  );
};

// ── First-time Welcome Modal (centered, 2x2 feature cards) ───────────────────
const WELCOME_FEATURES = [
  { icon: LayoutGrid, title: "Smart Dashboard", desc: "Real-time project health metrics and interactive insights." },
  { icon: Briefcase, title: "Your Projects", desc: "Track milestones, deliverables and progress in one place." },
  { icon: Receipt, title: "Invoices & Wallet", desc: "Streamlined invoice management and balance tracking." },
  { icon: HelpCircle, title: "Direct Support", desc: "One-click access to support and dedicated account management." },
];
const WelcomeModal = ({ name, onStartTour, onSkip }: { name: string; onStartTour: () => void; onSkip: () => void }) => (
  <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 backdrop-blur-sm p-4">
    <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
      <div className="px-8 pt-8 pb-6 text-center">
        <h2 className="text-[28px] font-extrabold text-on-surface tracking-tight">Welcome to your Workspace</h2>
        <p className="text-[14px] text-on-surface-variant mt-2 max-w-md mx-auto leading-relaxed">
          Experience total transparency and seamless collaboration. Your {useAuthStore.getState().authUser?.tenant?.companyName || "Omni Workspace"} portal brings together precision data and expert tools to accelerate your projects{name ? `, ${name.split(" ")[0]}` : ""}.
        </p>
      </div>
      <div className="px-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {WELCOME_FEATURES.map((f) => (
          <div key={f.title} className="flex items-start gap-3 rounded-xl border border-outline-variant p-4">
            <div className="w-9 h-9 rounded-lg bg-surface-container-low grid place-items-center text-primary shrink-0"><f.icon size={18} /></div>
            <div>
              <p className="text-[14px] font-bold text-on-surface leading-tight">{f.title}</p>
              <p className="text-[12px] text-on-surface-variant mt-0.5 leading-snug">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="px-8 mt-6 pt-5 border-t border-outline-variant text-center">
        <p className="text-[12px] text-on-surface-variant">Need help? Contact our support team 24/7 at <span className="text-primary font-semibold">{useAuthStore.getState().authUser?.tenant?.supportEmail || "support@omniworkspace.app"}</span></p>
      </div>
      <div className="px-8 py-6 flex items-center gap-3">
        <button onClick={onStartTour} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white text-[14px] font-bold rounded-xl hover:bg-[#004b9e]">
          Start Workspace Tour
        </button>
        <button onClick={onSkip} className="flex-1 px-5 py-3 border border-outline-variant text-on-surface-variant text-[14px] font-bold rounded-xl hover:bg-surface-container-low">
          Skip Tour
        </button>
      </div>
    </div>
  </div>
);

// ── Interactive Workspace Tour (spotlight over each sidebar item) ────────────
const TOUR_STEPS = [
  { title: "Dashboard", body: "Your home base — greeting, live clock, AI insights, project & milestone snapshots, the activity feed and meetings, all in one view." },
  { title: "Projects", body: "See every project's progress, milestones, delivered files and latest activity. Open a project for full details." },
  { title: "Services", body: "Browse the services available to you and request new work from your team." },
  { title: "Invoices", body: "Review invoices, track what's paid, pending or upcoming, and settle payments online." },
  { title: "Wallet", body: "Check your balance and any credits, with a full history of your wallet activity." },
  { title: "Inbox", body: "Message your project coordinator and keep every conversation in one thread." },
  { title: "Files", body: "Access all deliverables and documents shared throughout your projects." },
];
const WorkspaceTour = ({ navRefs, onClose }: { navRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>; onClose: () => void }) => {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const last = i === TOUR_STEPS.length - 1;

  // Measure the highlighted sidebar item on each step / resize.
  useEffect(() => {
    const measure = () => {
      const el = navRefs.current[i];
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [i, navRefs]);

  const step = TOUR_STEPS[i]!;
  const controls = (
    <>
      <div className="flex items-center gap-1.5 mb-4">
        {TOUR_STEPS.map((_, idx) => (
          <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-primary" : "w-1.5 bg-surface-container-high"}`} />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-[12px] font-semibold text-on-surface-variant hover:text-on-surface">Skip Tour</button>
        <div className="flex items-center gap-2">
          {i > 0 && <button onClick={() => setI(i - 1)} className="px-3 py-1.5 text-[12px] font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg">Previous</button>}
          <button onClick={() => (last ? onClose() : setI(i + 1))} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white text-[12px] font-semibold rounded-lg hover:bg-[#004b9e]">
            {last ? "Finish Tour" : "Next"} {!last && <ChevronRight size={14} />}
          </button>
        </div>
      </div>
    </>
  );

  // Desktop: spotlight the sidebar item with a tooltip beside it. Blocks all
  // interaction via four dark panels around the cut-out target.
  if (rect && rect.width > 0) {
    const pad = 6;
    const panel = "fixed bg-black/55 z-[85]";
    const tipTop = Math.min(rect.top, window.innerHeight - 260);
    return (
      <>
        <div className={panel} style={{ left: 0, top: 0, width: "100vw", height: rect.top - pad }} />
        <div className={panel} style={{ left: 0, top: rect.bottom + pad, width: "100vw", height: `calc(100vh - ${rect.bottom + pad}px)` }} />
        <div className={panel} style={{ left: 0, top: rect.top - pad, width: rect.left - pad, height: rect.height + pad * 2 }} />
        <div className={panel} style={{ left: rect.right + pad, top: rect.top - pad, width: `calc(100vw - ${rect.right + pad}px)`, height: rect.height + pad * 2 }} />
        {/* highlight ring */}
        <div className="fixed z-[86] rounded-lg ring-2 ring-primary ring-offset-2 pointer-events-none" style={{ left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
        {/* tooltip */}
        <div className="fixed z-[87] w-80 bg-white rounded-xl shadow-2xl p-5" style={{ left: rect.right + 18, top: tipTop }}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-primary">Step {i + 1} of {TOUR_STEPS.length}</p>
          <h3 className="text-[16px] font-bold text-on-surface mt-0.5">{step.title}</h3>
          <p className="text-[13px] text-on-surface-variant mt-1.5 leading-relaxed">{step.body}</p>
          <div className="mt-4">{controls}</div>
        </div>
      </>
    );
  }

  // Fallback (tablet / sidebar hidden): centered card.
  return (
    <div className="fixed inset-0 z-[85] grid place-items-center bg-black/55 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <p className="text-[11px] uppercase tracking-wider font-bold text-primary">Step {i + 1} of {TOUR_STEPS.length}</p>
        <h3 className="text-xl font-bold text-on-surface mt-0.5">{step.title}</h3>
        <p className="text-[14px] text-on-surface-variant mt-2 leading-relaxed">{step.body}</p>
        <div className="mt-5">{controls}</div>
      </div>
    </div>
  );
};

// ── small components ─────────────────────────────────────────────────────────
const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const relTimeLabel = (d?: string) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(m / 60), day = Math.floor(h / 24);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  if (h < 24) return `${h}h ago`;
  if (day === 1) return "Yesterday";
  return `${day} days ago`;
};
const Legend = ({ dot, label, value }: { dot: string; label: string; value: number }) => (
  <div className="flex items-center justify-between text-[13px]">
    <span className="flex items-center gap-2 text-on-surface-variant"><i className={`w-2 h-2 rounded-full ${dot} inline-block`} /> {label}</span>
    <span className="font-bold text-on-surface">{value}</span>
  </div>
);
const MiniStat = ({ label, value }: { label: string; value: number }) => (
  <div className="border-l-2 border-outline-variant pl-2">
    <p className="text-[11px] text-on-surface-variant">{label}</p>
    <p className="text-[16px] font-bold text-on-surface">{value}</p>
  </div>
);
const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="px-5 py-3 font-semibold whitespace-nowrap">{children}</th>
);

const ProjectDetailModal = ({ project, onClose }: { project: any; onClose: () => void }) => {
  const money2 = (n: any) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
  const ms = project.milestones || [];
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-outline-variant flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-on-surface">{project.name}</h2>
            <p className="text-[12px] text-on-surface-variant">{project.projectType}{project.projectSubType ? ` · ${project.projectSubType}` : ""}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-container-low text-on-surface-variant"><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4">
          {project.description && <p className="text-[13px] text-on-surface-variant leading-relaxed whitespace-pre-wrap">{project.description}</p>}
          <div className="grid grid-cols-2 gap-3">
            <Info label="Status" value={project.projectStatus} />
            <Info label="Payment" value={project.paymentStatus} />
            <Info label="Value" value={money2(project.projectPrice)} />
            <Info label="ETA" value={project.eta ? new Date(project.eta).toLocaleDateString() : "—"} />
          </div>
          {ms.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-outline mb-2">Milestones</p>
              <div className="space-y-1.5">
                {ms.map((m: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-[12px] bg-surface-container-low rounded-lg px-3 py-2">
                    <span className="text-on-surface flex items-center gap-2"><ChevronRight size={13} className="text-outline" /> {m.title || `Milestone ${i + 1}`}</span>
                    <span className="flex items-center gap-3"><span className="text-on-surface-variant">{m.status}</span><span className="font-bold">{money2(m.amount)}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(project.attachments) && project.attachments.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-outline mb-2">Files</p>
              <div className="space-y-1.5">
                {project.attachments.map((f: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] text-on-surface"><FileText size={14} className="text-primary" /> {f.name || "File"}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const Info = ({ label, value }: { label: string; value: any }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-outline">{label}</p>
    <p className="text-[13px] font-semibold text-on-surface">{value || "—"}</p>
  </div>
);

export default ClientPortalPage;
